'use client';

/**
 * CouponCardMini — mini coupon card for landing page (compact version).
 *
 * PR #1444 (v1244): Used by `<LandingFeaturedCoupons>` to render 3 featured
 * coupons in the landing grid. Extracted as a separate component (instead
 * of inline) for reusability + P-383 compliance (parent stays small).
 *
 * Layout (compact — smaller than full-size `/market/cupones` card):
 *   - Logo or icon (placeholder when no logo)
 *   - Title (sellerName + coupon title — 1 line truncated)
 *   - Discount badge (e.g., "20% OFF" or "$50 créditos")
 *   - Expiration countdown (e.g., "Expira en 3 días")
 *   - Whole card is a `<Link>` to `/market/cupones` (will prompt login
 *     when user tries to redeem)
 *
 * Click behavior:
 *   - Not logged in → navigates to `/market/cupones` (public catalog)
 *     → if user clicks "Redimir" → redirect to /login?return=...
 *   - Logged in → navigates to `/market/cupones` → can redeem directly
 *
 * Anti-P-041: typed via `FeaturedCoupon` interface.
 * Anti-P-042: silent fail (no console).
 *
 * Spec ref: SPEC-242 Track C #15 (Cuponera), SPEC-NEGOCIO §10.13.
 */

import Link from 'next/link';
import type { Route } from 'next';
import { Ticket, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface FeaturedCoupon {
  _id: string;
  title: string;
  discountType: 'percentage' | 'fixed';
  discountValue: string;
  expiresAt: string | null;
  seller: {
    _id: string;
    storeName: string;
    storeSlug: string;
  } | null;
}

interface CouponCardMiniProps {
  coupon: FeaturedCoupon;
  className?: string;
}

/**
 * Format discount for display.
 * - percentage: "20% OFF"
 * - fixed: "$50 créditos OFF"
 */
function formatDiscount(coupon: FeaturedCoupon): string {
  if (coupon.discountType === 'percentage') {
    return `${coupon.discountValue}% OFF`;
  }
  return `${coupon.discountValue} créditos OFF`;
}

/**
 * Format expiration as countdown text.
 * - null → "Sin vencimiento"
 * - < 24h → "Expira hoy"
 * - < 7d → "Expira en N días"
 * - else → "Expira en N semanas"
 */
function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'Sin vencimiento';
  const expiry = new Date(expiresAt).getTime();
  const now = Date.now();
  const diffMs = expiry - now;
  if (diffMs <= 0) return 'Expirado';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days === 0) return 'Expira hoy';
  if (days < 7) return `Expira en ${days} día${days === 1 ? '' : 's'}`;
  const weeks = Math.floor(days / 7);
  return `Expira en ${weeks} semana${weeks === 1 ? '' : 's'}`;
}

export function CouponCardMini({ coupon, className }: CouponCardMiniProps) {
  const storeName = coupon.seller?.storeName ?? 'Comercio Profileia';
  const href = '/market/cupones' as Route;

  return (
    <Link
      href={href}
      className={cn(
        'group block rounded-2xl border border-border bg-card p-5 transition-all hover:shadow-lg hover:border-primary/30',
        className,
      )}
      aria-label={`Cupón ${coupon.title} de ${storeName} — ${formatDiscount(coupon)}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Ticket className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground truncate mb-0.5">
            {storeName}
          </p>
          <h3 className="font-semibold text-sm line-clamp-2 leading-tight">
            {coupon.title}
          </h3>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
          {formatDiscount(coupon)}
        </Badge>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3" />
          {formatExpiry(coupon.expiresAt)}
        </span>
      </div>
    </Link>
  );
}
